// OrderPopup.jsx — Trade popup with market depth, margin info, and action buttons
// Used by Market Watch (new orders), Positions (add more / square off), and Orders (modify)
//
// mode="new"       — Default. Shows margin, BUY + SELL buttons. Places a new order.
// mode="addMore"   — Single directional button (SELL MORE or BUY MORE). Places a new order.
// mode="squareOff" — Shows premium to pay (no margin). Single opposite-direction button.
// mode="modify"    — Shows current order details + margin. Modify + Cancel buttons.
//                    Modify updates the EXISTING order (does NOT place a new one).

import React, { useState, useRef } from 'react';
import axios from 'axios';
import { fmtINR, fmtNum } from './calculations';

const API_BASE = '/api';

function OrderPopup({
  themeCtx, row, orderLots, setOrderLots, orderPrice, setOrderPrice,
  orderMarginData, orderAccount, onAccountChange, accountMargins, margins,
  onRefetchMargin, onClose,
  mode = 'new',              // 'new' | 'addMore' | 'squareOff' | 'modify'
  positionDirection = null,  // 'BUY' or 'SELL' — used in addMore/squareOff
  existingOrder = null,      // The order being modified — { orderId, quantity, price, filledQuantity, variety, transactionType, tradingsymbol, exchange, account, accountLabel }
  onOrderChanged = null,      // Callback after modify/cancel succeeds (refreshes order list)
  user = null                 // Current logged-in user — { role, accountAccess: [{ accountId, canTrade }] }
}) {
  const isSquareOff = mode === 'squareOff';
  const isModify = mode === 'modify';
  const { t, isDark } = themeCtx;

  // ─── Order placement / modify state ───
  const [confirmAction, setConfirmAction] = useState(null); // null | 'BUY' | 'SELL' | 'MODIFY' | 'CANCEL_ORDER'
  const [orderStatus, setOrderStatus] = useState(null);     // null | 'placing' | 'success' | 'error'
  const [orderResult, setOrderResult] = useState('');        // success/error message
  const confirmTimer = useRef(null);

  function startConfirm(action) {
    setConfirmAction(action);
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    confirmTimer.current = setTimeout(() => setConfirmAction(null), 5000);
  }

  function cancelConfirm() {
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    setConfirmAction(null);
  }

  async function executeOrder() {
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    const action = confirmAction;
    setConfirmAction(null);

    // Safety gate: never allow an order without a real price
    const finalPrice = parseFloat(orderPrice) || 0;
    if (finalPrice <= 0 && action !== 'CANCEL_ORDER') {
      setOrderStatus('error');
      setOrderResult('Price must be greater than 0. MARKET orders are not allowed.');
      return;
    }

    setOrderStatus('placing');
    try {
      if (action === 'MODIFY' && existingOrder) {
        // ── MODIFY: update existing order, do NOT place a new one ──
        const res = await axios.post(`${API_BASE}/orders/modify`, {
          accountId: existingOrder.account,
          orderId: existingOrder.orderId,
          quantity: lots * (row.lotSize || 1),
          price: finalPrice,
          tradingsymbol: existingOrder.tradingsymbol || row.tradingsymbol,
          exchange: existingOrder.exchange || row.exchange || 'NFO',
          transactionType: existingOrder.transactionType,
          variety: existingOrder.variety || 'regular'
        });
        setOrderStatus('success');
        setOrderResult(res.data.message || `Order modified (ID: ${existingOrder.orderId})`);
        if (onOrderChanged) onOrderChanged();
      } else if (action === 'CANCEL_ORDER' && existingOrder) {
        // ── CANCEL: cancel the existing order ──
        const res = await axios.post(`${API_BASE}/orders/cancel`, {
          accountId: existingOrder.account,
          orderId: existingOrder.orderId,
          variety: existingOrder.variety || 'regular'
        });
        setOrderStatus('success');
        setOrderResult(`Order ${existingOrder.orderId} cancelled`);
        if (onOrderChanged) onOrderChanged();
      } else {
        // ── PLACE NEW: BUY or SELL ──
        const res = await axios.post(`${API_BASE}/orders/place`, {
          accountId: orderAccount,
          tradingsymbol: row.tradingsymbol,
          exchange: row.exchange || 'NFO',
          transactionType: action,
          quantity: lots * (row.lotSize || 1),
          price: finalPrice,
          orderType: 'LIMIT',
          product: 'NRML'
        });
        setOrderStatus('success');
        setOrderResult(res.data.message || `Order placed (ID: ${res.data.orderId})`);
      }
    } catch (err) {
      setOrderStatus('error');
      setOrderResult(err.response?.data?.error || err.message || 'Order failed');
    }
  }

  const lots = Math.max(1, parseInt(orderLots) || 1);
  const price = parseFloat(orderPrice) || 0;
  const priceValid = price > 0;

  // For modify mode: detect if anything actually changed
  const modifyHasChanges = isModify && existingOrder
    ? (lots * (row.lotSize || 1)) !== existingOrder.quantity || price !== existingOrder.price
    : false;

  // Margin calculations
  const cachedFallback = Math.round((margins[row.tradingsymbol]?.totalMargin || row.margin || 0) * lots);
  const standaloneTotal = orderMarginData ? (orderMarginData.marginWithoutHedge || 0) : cachedFallback;
  const hedgeBenefit = orderMarginData?.hedgeBenefit || 0;
  const totalMargin = orderMarginData ? (orderMarginData.marginWithHedge || orderMarginData.marginWithoutHedge || 0) : cachedFallback;
  const totalPremium = Math.round(price * (row.lotSize || 1) * lots);
  const returnPct = totalMargin > 0 ? +((totalPremium / totalMargin) * 100).toFixed(2) : 0;
  const dte = row.daysToExpiry || 0;
  const annualized = dte > 0 && returnPct > 0 ? +((Math.pow(1 + Math.abs(returnPct / 100), 365 / dte) - 1) * 100).toFixed(1) : 0;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: t.bgCard, borderRadius: 8, width: 420, border: `1px solid ${t.border}`, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{row.symbol}</span>
            <span style={{ color: t.textMuted, fontSize: 12, marginLeft: 8 }}>{row.strike} {row.type}</span>
            <span style={{ color: t.textDim, fontSize: 11, marginLeft: 8 }}>{row.expiryStr}</span>
            {isSquareOff && (
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: isDark ? '#2e2a1a' : '#fff7ed', color: t.yellow }}>SQUARE OFF</span>
            )}
            {mode === 'addMore' && (
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: isDark ? '#1a2e1a' : '#f0fdf4', color: t.green }}>ADD MORE</span>
            )}
            {isModify && (
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: isDark ? '#1a1d2a' : '#eff6ff', color: t.blue }}>MODIFY</span>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.textMuted, fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Live price + Order ID (modify mode) */}
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <div>
            <span style={{ color: t.textMuted }}>LTP</span>
            <span style={{ fontWeight: 600, marginLeft: 6 }}>₹{row.ltp}</span>
          </div>
          {isModify && existingOrder && (
            <div>
              <span style={{ color: t.textMuted }}>Order ID </span>
              <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{existingOrder.orderId}</span>
            </div>
          )}
        </div>

        {/* Market Depth — Top 5 Buy/Sell levels */}
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${t.border}`, fontSize: 11 }}>
          <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 6, color: t.textMuted }}>Market Depth</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: t.textDim, fontSize: 10 }}>
                <span>Qty</span><span>Bid</span>
              </div>
              {(row.depth?.buy || []).slice(0, 5).map((level, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <span style={{ color: t.textMuted }}>{level.quantity ? fmtNum(level.quantity) : '—'}</span>
                  <span style={{ color: t.green }}>{level.price || '—'}</span>
                </div>
              ))}
              {(!row.depth?.buy || row.depth.buy.length === 0) && (
                <div style={{ color: t.textDim, textAlign: 'center' }}>—</div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: t.textDim, fontSize: 10 }}>
                <span>Ask</span><span>Qty</span>
              </div>
              {(row.depth?.sell || []).slice(0, 5).map((level, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <span style={{ color: t.red }}>{level.price || '—'}</span>
                  <span style={{ color: t.textMuted }}>{level.quantity ? fmtNum(level.quantity) : '—'}</span>
                </div>
              ))}
              {(!row.depth?.sell || row.depth.sell.length === 0) && (
                <div style={{ color: t.textDim, textAlign: 'center' }}>—</div>
              )}
            </div>
          </div>
        </div>

        {/* Current order details (modify mode only) */}
        {isModify && existingOrder && (
          <div style={{ padding: '8px 16px', borderBottom: `1px solid ${t.border}`, fontSize: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 10, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Current Order</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ color: t.textMuted }}>Direction</span>
              <span style={{ fontWeight: 600, color: existingOrder.transactionType === 'BUY' ? t.green : t.red }}>{existingOrder.transactionType}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ color: t.textMuted }}>Quantity</span>
              <span>{fmtNum(existingOrder.quantity)}{row.lotSize > 1 ? ` (${Math.round(existingOrder.quantity / row.lotSize)} lots)` : ''}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ color: t.textMuted }}>Price</span>
              <span>₹{Number(existingOrder.price).toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: t.textMuted }}>Filled</span>
              <span>{fmtNum(existingOrder.filledQuantity || 0)} / {fmtNum(existingOrder.quantity)}</span>
            </div>
          </div>
        )}

        {/* Lots + Price inputs */}
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${t.border}`, fontSize: 12, display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: t.textMuted, marginBottom: 4 }}>{isModify ? 'New Lots' : 'Lots'} <span style={{ color: t.textDim }}>(1 lot = {row.lotSize} qty)</span></div>
            <input
              type="number" min="1" step="1"
              value={orderLots}
              onChange={e => {
                const v = Math.max(1, parseInt(e.target.value) || 1);
                setOrderLots(v);
                if (!isSquareOff && onRefetchMargin) onRefetchMargin(v, parseFloat(orderPrice) || row.ltp);
              }}
              style={{ width: '100%', padding: '7px 10px', fontSize: 13, fontWeight: 600, background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 5, color: t.text, outline: 'none' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: t.textMuted, marginBottom: 4 }}>{isModify ? 'New Price' : 'Price'} <button onClick={() => setOrderPrice(String(row.ltp))} style={{ marginLeft: 6, fontSize: 10, color: t.blue, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>↺ LTP</button></div>
            <input
              type="number" min="0" step="0.05"
              value={orderPrice}
              onChange={e => setOrderPrice(e.target.value)}
              style={{ width: '100%', padding: '7px 10px', fontSize: 13, fontWeight: 600, background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 5, color: t.text, outline: 'none' }}
            />
          </div>
        </div>

        {/* Margin & Returns (new/addMore/modify) OR Premium to Pay (squareOff) */}
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${t.border}`, fontSize: 12 }}>
          {isSquareOff ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 10, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Square Off Cost</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: t.textMuted }}>{lots} lot{lots > 1 ? 's' : ''} × {row.lotSize} qty × ₹{Number(price).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${t.border}`, paddingTop: 5, marginTop: 2 }}>
                <span style={{ color: t.textMuted, fontWeight: 600 }}>Premium to pay</span>
                <span style={{ fontWeight: 700, color: t.red }}>{fmtINR(totalPremium)}</span>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 10, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Margin & Returns</span>
                {orderMarginData
                  ? <span style={{ fontSize: 10, color: t.green }}>● live</span>
                  : <span style={{ fontSize: 10, color: t.yellow }}>● loading...</span>
                }
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: t.textMuted }}>Standalone — {lots} lot{lots > 1 ? 's' : ''}</span>
                <span>{fmtINR(standaloneTotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: t.textMuted }}>Hedge benefit</span>
                {hedgeBenefit > 0
                  ? <span style={{ color: t.green }}>-{fmtINR(hedgeBenefit)}</span>
                  : <span style={{ color: t.textDim }}>{orderMarginData ? '— (no hedge)' : '—'}</span>
                }
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${t.border}`, paddingTop: 5, marginTop: 2 }}>
                <span style={{ color: t.textMuted, fontWeight: 600 }}>Net margin required</span>
                <span style={{ fontWeight: 700, color: t.yellow }}>{fmtINR(totalMargin)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <span style={{ color: t.textMuted }}>Premium ({lots} lot{lots > 1 ? 's' : ''} × {row.lotSize})</span>
                <span style={{ color: t.green }}>{fmtINR(totalPremium)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={{ color: t.textMuted }}>Return</span>
                <span>{returnPct}% {annualized > 0 ? `(${annualized}% ann.)` : ''}</span>
              </div>
            </>
          )}
        </div>

        {/* Order status feedback */}
        {orderStatus && (
          <div style={{ padding: '10px 16px', fontSize: 12, textAlign: 'center',
            background: orderStatus === 'success' ? (isDark ? '#1a2e1a' : '#f0fdf4') : orderStatus === 'error' ? (isDark ? '#2e1a1a' : '#fef2f2') : (isDark ? '#1a1d2a' : '#f0f4ff'),
            color: orderStatus === 'success' ? t.green : orderStatus === 'error' ? t.red : t.blue
          }}>
            {orderStatus === 'placing' && <span style={{ fontWeight: 600 }}>{isModify ? 'Processing...' : 'Placing order...'}</span>}
            {orderStatus === 'success' && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>✅ {orderResult}</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>{isModify ? '' : 'You can close this popup now.'}</div>
              </div>
            )}
            {orderStatus === 'error' && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>❌ {isModify ? 'Failed' : 'Order Failed'}</div>
                <div style={{ fontSize: 11 }}>{orderResult}</div>
              </div>
            )}
          </div>
        )}

        {/* Account + Action buttons */}
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Account selector — hidden in modify mode (account is fixed) */}
          {/* For traders: only show accounts they have trade access to */}
          {/* For admins/viewers: show all accounts */}
          {!isModify && (() => {
            const allAccounts = Object.entries(accountMargins);
            const filteredAccounts = (user?.role === 'trader' && user?.accountAccess)
              ? allAccounts.filter(([id]) => user.accountAccess.some(a => a.accountId === id && a.canTrade))
              : allAccounts;
            return (
              <select
                value={orderAccount}
                onChange={e => onAccountChange(e.target.value)}
                disabled={!!orderStatus}
                style={{ flex: 1, padding: '8px 10px', fontSize: 12, background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 5, color: t.text, outline: 'none', opacity: orderStatus ? 0.5 : 1 }}
              >
                {filteredAccounts.map(([id, acct]) => (
                  <option key={id} value={id}>{acct.label || id} — {fmtINR(acct.available || 0)} avail.</option>
                ))}
                {filteredAccounts.length === 0 && (
                  <option value="" disabled>No accounts available for trading</option>
                )}
              </select>
            );
          })()}
          {isModify && existingOrder && (
            <span style={{ flex: 1, fontSize: 12, color: t.textMuted }}>{existingOrder.accountLabel || existingOrder.account}</span>
          )}

          {/* Action buttons by state */}
          {orderStatus === 'placing' ? (
            <button disabled style={{ padding: '8px 24px', fontSize: 12, fontWeight: 700, background: '#6b7280', color: '#fff', border: 'none', borderRadius: 5, cursor: 'not-allowed', flex: 'none', opacity: 0.5 }}>
              {isModify ? 'Processing...' : 'Placing...'}
            </button>
          ) : orderStatus === 'success' ? (
            <button onClick={onClose} style={{ padding: '8px 24px', fontSize: 12, fontWeight: 700, background: t.blue, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', flex: 'none' }}>
              Close
            </button>
          ) : orderStatus === 'error' ? (
            <button onClick={() => { setOrderStatus(null); setOrderResult(''); }} style={{ padding: '8px 24px', fontSize: 12, fontWeight: 700, background: '#f97316', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', flex: 'none' }}>
              Try Again
            </button>
          ) : !priceValid && !isModify ? (
            <span style={{ fontSize: 11, color: t.red, fontWeight: 600 }}>Enter a price to place order</span>
          ) : isModify ? (
            /* ── MODIFY MODE: Modify + Cancel buttons ── */
            <>
              {priceValid ? (
                <button onClick={() => startConfirm('MODIFY')} disabled={!modifyHasChanges}
                  style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700,
                    background: modifyHasChanges ? t.blue : '#4a5568', color: '#fff',
                    border: 'none', borderRadius: 5,
                    cursor: modifyHasChanges ? 'pointer' : 'not-allowed',
                    opacity: modifyHasChanges ? 1 : 0.5
                  }}>Modify</button>
              ) : (
                <span style={{ fontSize: 11, color: t.red, fontWeight: 600 }}>Enter a price</span>
              )}
              <button onClick={() => startConfirm('CANCEL_ORDER')}
                style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700, background: t.red, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>
                Cancel Order
              </button>
            </>
          ) : isSquareOff ? (
            (() => {
              const action = positionDirection === 'SELL' ? 'BUY' : 'SELL';
              const label = positionDirection === 'SELL' ? 'BUY TO CLOSE' : 'SELL TO CLOSE';
              const bg = action === 'BUY' ? '#16a34a' : '#dc2626';
              return <button onClick={() => startConfirm(action)} style={{ padding: '8px 24px', fontSize: 12, fontWeight: 700, background: bg, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', flex: 'none' }}>{label}</button>;
            })()
          ) : mode === 'addMore' && positionDirection ? (
            (() => {
              const action = positionDirection;
              const label = positionDirection === 'SELL' ? 'SELL MORE' : 'BUY MORE';
              const bg = action === 'BUY' ? '#16a34a' : '#dc2626';
              return <button onClick={() => startConfirm(action)} style={{ padding: '8px 24px', fontSize: 12, fontWeight: 700, background: bg, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', flex: 'none' }}>{label}</button>;
            })()
          ) : (
            <>
              <button onClick={() => startConfirm('BUY')} style={{ padding: '8px 18px', fontSize: 12, fontWeight: 700, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>BUY</button>
              <button onClick={() => startConfirm('SELL')} style={{ padding: '8px 18px', fontSize: 12, fontWeight: 700, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>SELL</button>
            </>
          )}
        </div>

        {/* Confirmation mini-popup */}
        {confirmAction && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, zIndex: 10 }}
            onClick={cancelConfirm}
          >
            <div onClick={e => e.stopPropagation()} style={{
              background: t.bgCard, borderRadius: 8, padding: '20px 24px', width: 320,
              border: `2px solid ${confirmAction === 'MODIFY' ? t.blue : confirmAction === 'CANCEL_ORDER' ? t.red : confirmAction === 'BUY' ? '#16a34a' : '#dc2626'}`,
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12,
                color: confirmAction === 'MODIFY' ? t.blue : confirmAction === 'CANCEL_ORDER' ? t.red : confirmAction === 'BUY' ? t.green : t.red
              }}>
                {confirmAction === 'MODIFY' ? 'Confirm Modify' : confirmAction === 'CANCEL_ORDER' ? 'Confirm Cancel' : `Confirm ${confirmAction} Order`}
              </div>
              <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Instrument</span>
                  <span style={{ color: t.text, fontWeight: 600 }}>{row.tradingsymbol}</span>
                </div>
                {confirmAction === 'MODIFY' && existingOrder ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Quantity</span>
                      <span style={{ color: t.text, fontWeight: 600 }}>
                        {existingOrder.quantity !== lots * (row.lotSize || 1)
                          ? `${fmtNum(existingOrder.quantity)} → ${fmtNum(lots * (row.lotSize || 1))}`
                          : fmtNum(lots * (row.lotSize || 1))}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Price</span>
                      <span style={{ color: t.text, fontWeight: 600 }}>
                        {existingOrder.price !== price
                          ? `₹${Number(existingOrder.price).toFixed(2)} → ₹${price.toFixed(2)}`
                          : `₹${price.toFixed(2)}`}
                      </span>
                    </div>
                  </>
                ) : confirmAction === 'CANCEL_ORDER' && existingOrder ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Order ID</span>
                    <span style={{ color: t.text, fontWeight: 600, fontFamily: 'monospace', fontSize: 10 }}>{existingOrder.orderId}</span>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Quantity</span>
                      <span style={{ color: t.text, fontWeight: 600 }}>{lots} lot{lots > 1 ? 's' : ''} ({lots * (row.lotSize || 1)} qty)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Price</span>
                      <span style={{ color: t.text, fontWeight: 600 }}>₹{Number(price).toFixed(2)} (LIMIT)</span>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Account</span>
                  <span style={{ color: t.text, fontWeight: 600 }}>
                    {isModify ? (existingOrder?.accountLabel || existingOrder?.account) : (accountMargins[orderAccount]?.label || orderAccount)}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button onClick={cancelConfirm}
                  style={{ flex: 1, padding: '8px', fontSize: 12, fontWeight: 600, background: 'transparent', color: t.textMuted, border: `1px solid ${t.borderInput}`, borderRadius: 5, cursor: 'pointer' }}
                >{isModify ? 'Go Back' : 'Cancel'}</button>
                <button onClick={executeOrder}
                  style={{ flex: 1, padding: '8px', fontSize: 12, fontWeight: 700,
                    background: confirmAction === 'MODIFY' ? t.blue : confirmAction === 'CANCEL_ORDER' ? t.red : confirmAction === 'BUY' ? '#16a34a' : '#dc2626',
                    color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer'
                  }}
                >{confirmAction === 'MODIFY' ? 'Confirm Modify' : confirmAction === 'CANCEL_ORDER' ? 'Confirm Cancel' : `Confirm ${confirmAction}`}</button>
              </div>
              <div style={{ fontSize: 10, color: t.textDim, marginTop: 8, textAlign: 'center' }}>Auto-cancels in 5 seconds</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderPopup;
